import { selectCurrentStage, useJourneyStore } from "../game/state/journeyMachine";
import { JOURNEY_STAGES } from "../data/journeyStages";
import { EvidenceTag } from "../evidence/EvidenceTag";
import { StageNav } from "./StageNav";
import { useHotspotStore } from "../game/state/hotspotSlice";
import { SEDE_HOTSPOTS } from "../game/scenes/sede/sedeHotspots";
import { VehicleControls } from "./VehicleControls";
import { NationalPanel } from "../game/scenes/national/NationalPanel";
import { FECHA_CORTE_MUNICIPIOS, FUENTE_MUNICIPIOS } from "../data/colombia";

/**
 * Panel lateral con la información de la etapa activa, la ficha del
 * hotspot seleccionado (con EvidenceTag), los controles educativos del
 * vehículo en pista y la navegación. En modo "red-nacional" la cabecera
 * y el contenido son propios (sin contador de etapas ni StageNav del
 * recorrido).
 */
export function SidePanel() {
  const stage = useJourneyStore(selectCurrentStage);
  const stageIndex = useJourneyStore((s) => s.stageIndex);
  const viewMode = useJourneyStore((s) => s.viewMode);
  const setViewMode = useJourneyStore((s) => s.setViewMode);
  const mode = useJourneyStore((s) => s.mode);
  const esNacional = mode === "red-nacional";

  return (
    <aside
      aria-label={esNacional ? "Panel de la red nacional" : "Información de la etapa"}
      className="flex w-full flex-col gap-4 overflow-auto border-t border-[var(--cale-border)] bg-[var(--cale-surface-raised)] p-4 md:w-96 md:border-l md:border-t-0"
    >
      {esNacional ? (
        <>
          <div>
            <h2 className="text-xl font-semibold">Red nacional CALE</h2>
            <p className="text-xs text-[var(--cale-ink-muted)]">
              Sala de análisis — simulación educativa
            </p>
          </div>
          <NationalPanel />
          <p className="mt-auto text-xs text-[var(--cale-ink-muted)]">
            Fuente: {FUENTE_MUNICIPIOS}. Fecha de corte: {FECHA_CORTE_MUNICIPIOS}.
          </p>
        </>
      ) : (
        <>
          <div className="flex items-start justify-between gap-2">
            <div>
              <p className="text-xs text-[var(--cale-ink-muted)]">
                Etapa {stageIndex + 1} de {JOURNEY_STAGES.length}
              </p>
              <h2 className="text-xl font-semibold">{stage.title}</h2>
            </div>
            <EvidenceTag kind={stage.tag} />
          </div>
          <p className="text-sm">{stage.objective}</p>
          <div role="group" aria-label="Modo de vista" className="flex gap-1 rounded-md border border-[var(--cale-border)] p-1">
            {(["3d", "2d"] as const).map((v) => (
              <button
                key={v}
                type="button"
                onClick={() => setViewMode(v)}
                aria-pressed={viewMode === v}
                className={`flex-1 rounded px-3 py-1 text-sm ${
                  viewMode === v ? "bg-[var(--cale-accent)] text-white" : "hover:bg-[var(--cale-surface)]"
                }`}
              >
                {v === "3d" ? "Vista 3D" : "Vista 2D accesible"}
              </button>
            ))}
          </div>
          <HotspotPanel />
          {(stage.id === "destreza-auto" || stage.id === "via-publica") && <VehicleControls />}
          <StageNav />
          <p className="mt-auto text-xs text-[var(--cale-ink-muted)]">Fuente: {stage.source}</p>
        </>
      )}
    </aside>
  );
}

/**
 * Ficha del hotspot activo + lista de puntos de interés como botones
 * (acceso por teclado equivalente al clic sobre los marcadores 3D).
 */
function HotspotPanel() {
  const selectedId = useHotspotStore((s) => s.selectedId);
  const open = useHotspotStore((s) => s.open);
  const close = useHotspotStore((s) => s.close);
  const selected = SEDE_HOTSPOTS.find((h) => h.id === selectedId);

  return (
    <section aria-label="Puntos de interés de la sede" className="flex flex-col gap-2">
      <h3 className="text-sm font-semibold">Puntos de interés</h3>
      <ul className="flex flex-wrap gap-1.5">
        {SEDE_HOTSPOTS.map((h) => (
          <li key={h.id}>
            <button
              type="button"
              onClick={() => (selectedId === h.id ? close() : open(h.id))}
              aria-pressed={selectedId === h.id}
              className={`rounded-md border px-2 py-1 text-xs ${
                selectedId === h.id
                  ? "border-[var(--cale-accent)] bg-[var(--cale-accent)] text-white"
                  : "border-[var(--cale-border)] hover:bg-[var(--cale-surface)]"
              }`}
            >
              {h.label}
            </button>
          </li>
        ))}
      </ul>
      {selected && (
        <article
          aria-live="polite"
          className="flex flex-col gap-1.5 rounded-md border border-[var(--cale-border)] bg-[var(--cale-surface)] p-3"
        >
          <div className="flex items-start justify-between gap-2">
            <h4 className="text-sm font-semibold">{selected.label}</h4>
            <EvidenceTag kind={selected.tag} />
          </div>
          <p className="text-sm">{selected.description}</p>
          <p className="text-xs text-[var(--cale-ink-muted)]">Fuente: {selected.source}</p>
          <button
            type="button"
            onClick={close}
            className="self-start rounded-md border border-[var(--cale-border)] px-2 py-1 text-xs hover:bg-[var(--cale-surface-raised)]"
          >
            Cerrar ficha
          </button>
        </article>
      )}
    </section>
  );
}
